import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Phone,
  Video,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
  Flame,
  HeartPulse,
  Car,
  Shield,
  Camera,
  RotateCcw,
  XCircle,
} from "lucide-react";
import api from "../api/axios";
import { useGeolocation } from "../hooks/useGeolocation";
import { useMediaRecorder } from "../hooks/useMediaRecorder";
import ProgressBar from "../components/shared/ProgressBar";
import StickyCallBar from "../components/shared/StickyCallBar";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";

const STEP_KEYS = ["emergency.step1", "emergency.step2", "emergency.step3", "emergency.step4"];

const EMERGENCY_TYPES = [
  {
    type: "medical",
    icon: HeartPulse,
    label: "Medical",
    labelHi: "चिकित्सा",
    number: "108",
    color: "bg-red-600 hover:bg-red-700 border-red-600",
    desc: "Ambulance / एम्बुलेंस",
    numberDesc: "108 — Ambulance: Medical emergency, need ambulance (पूरे भारत में एम्बुलेंस)",
  },
  {
    type: "accident",
    icon: Car,
    label: "Accident",
    labelHi: "दुर्घटना",
    number: "112",
    color: "bg-orange-600 hover:bg-orange-700 border-orange-600",
    desc: "ERSS / आपातकालीन",
    numberDesc: "112 — National Emergency: Accidents, rescue, any emergency (भारत का एकीकृत आपातकाल नंबर)",
  },
  {
    type: "fire",
    icon: Flame,
    label: "Fire / Other",
    labelHi: "आग / अन्य",
    number: "112",
    color: "bg-amber-600 hover:bg-amber-700 border-amber-600",
    desc: "Fire & Rescue / अग्निशमन",
    numberDesc: "112 — Fire & Rescue: Fire, building collapse, other (आग, बचाव सेवा)",
  },
];

const ReportEmergencyPage = () => {
  const { locketId } = useParams();
  const id = locketId;
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [callInitiated, setCallInitiated] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [manualAddress, setManualAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [, setReportId] = useState(null);

  const { location, error: geoError, loading: geoLoading, requestLocation } = useGeolocation();
  const {
    recording,
    videoBlob,
    videoUrl,
    error: videoError,
    timeLeft,
    startRecording,
    stopRecording,
    resetRecording,
    streamRef,
  } = useMediaRecorder(10000);

  const videoPreviewRef = useRef(null);

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  useEffect(() => {
    if (recording && streamRef.current && videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = streamRef.current;
    }
  }, [recording, streamRef]);

  const handleEmergencyTypeSelect = (emergencyType) => {
    setSelectedType(emergencyType);
    setCallInitiated(true);
    window.location.href = `tel:${emergencyType.number}`;
    setTimeout(() => setStep(2), 2000);
  };

  const validatePhone = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length !== 10) return t("emergency.phoneValidationDigits");
    if (!/^[6-9]/.test(cleaned)) return t("emergency.phoneValidationStart");
    return "";
  };

  const handleSubmit = async () => {
    if (step === 3) {
      const err = validatePhone(phoneNumber);
      if (err) {
        setPhoneError(err);
        return;
      }
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      // Convert video blob to base64 data URL for storage (optional)
      let videoDataUrl = null;
      if (videoBlob && videoBlob.size > 0) {
        // Guard: base64 encoding adds ~33% overhead; server limit is 15 MB
        if (videoBlob.size > 10 * 1024 * 1024) {
          setSubmitError("Video is too large (max 10 MB). Please record a shorter clip or skip the video.");
          setSubmitting(false);
          return;
        }
        videoDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(videoBlob);
        });
      }

      const payload = {
        qrId: id,
        reporterPhone: phoneNumber.replace(/\D/g, ""),
        emergencyType: selectedType?.type || "medical",
        latitude: location?.latitude ?? null,
        longitude: location?.longitude ?? null,
        address: manualAddress || "",
      };
      if (videoDataUrl) payload.video = videoDataUrl;

      const res = await api.post("/emergency-report", payload);

      setReportId(res.data?.id);
      setSubmitted(true);

      if (res.data?.id) {
        navigate(`/${id}/rescue?reportId=${res.data.id}`);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t("emergency.submitError");
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md border border-emerald-200 p-8 text-center max-w-sm w-full">
          <Loader2 size={36} className="text-emerald-600 mx-auto mb-3 animate-spin" />
          <h1 className="text-lg font-bold text-slate-900">{t("emergency.redirecting")}</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <div className="sticky top-0 z-40 bg-red-600 text-white shadow-md">
        <div className="max-w-lg mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => (step > 0 ? setStep(step - 1) : navigate(`/${id}`))} className="p-2 -m-2 text-white hover:bg-white/10 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Back">
            <ArrowLeft size={22} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate">{t("emergency.reportTitle")}</p>
            <p className="text-xs text-white/80 truncate hidden sm:block">{t("emergency.reportSubtitle")}</p>
          </div>
          <a href="tel:112" className="bg-white/20 rounded-lg px-2.5 py-2 text-xs font-bold border border-white/30 whitespace-nowrap min-h-[44px] flex items-center justify-center shrink-0" title="112 - India's single national emergency number">
            {t("emergency.emergency112")}
          </a>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-3 pt-2">
        <ProgressBar currentStep={step + 1} totalSteps={4} labels={STEP_KEYS.map((k) => t(k))} />
      </div>

      <div className="max-w-lg mx-auto px-3 pt-3 pb-4">
        {step === 0 && (
          <div className="animate-fade-in">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">{t("emergency.step1")}</h2>
            <p className="text-xs text-slate-500 mb-4 sm:mb-6">{t("emergency.needLocationDesc")}</p>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 mb-3">
              {geoLoading ? (
                <div className="flex items-center gap-3 text-slate-600">
                  <Loader2 size={20} className="animate-spin" />
                  <div>
                    <p className="text-sm font-semibold">{t("emergency.capturingLocation")}</p>
                  </div>
                </div>
              ) : location ? (
                <div>
                  <div className="flex items-center gap-2 text-emerald-600 mb-3">
                    <CheckCircle size={20} />
                    <div>
                      <p className="text-sm font-bold">{t("emergency.locationCaptured")}</p>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 font-mono">
                    <MapPin size={14} className="inline mr-1" />
                    {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 text-amber-600 mb-3">
                    <AlertTriangle size={20} />
                    <div>
                      <p className="text-sm font-bold">{geoError || t("emergency.locationUnavailable")}</p>
                    </div>
                  </div>
                  <button
                    onClick={requestLocation}
                    className="text-sm text-red-600 font-semibold hover:text-red-700 transition-colors"
                  >
                    {t("emergency.tryAgain")}
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 mb-3">
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                {t("emergency.manualAddressLabel")}
              </label>
              <textarea
                value={manualAddress}
                onChange={(e) => setManualAddress(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 min-h-[64px] resize-none"
                placeholder={t("emergency.manualAddressPlaceholder")}
              />
            </div>

            <button
              onClick={() => setStep(1)}
              disabled={!location && !manualAddress}
              className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold py-3 min-h-[48px] rounded-lg shadow-md transition-colors duration-150"
            >
              {t("emergency.nextSelectType")}
              <ArrowRight size={18} />
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-in">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">{t("emergency.step2")}</h2>
            <p className="text-xs text-slate-500 mb-3">{t("emergency.selectTypeDesc")}</p>

            <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 mb-3 sm:mb-4">
              <p className="text-sm font-bold text-slate-700 mb-2">{t("emergency.indiaEmergencyNumbers")}</p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li><strong>112</strong> — {t("emergency.number112Desc")}</li>
                <li><strong>108</strong> — {t("emergency.number108Desc")}</li>
                <li><strong>100</strong> — {t("emergency.number100Desc")}</li>
              </ul>
            </div>

            <div className="space-y-3 mb-6">
              {EMERGENCY_TYPES.map((etype) => {
                const Icon = etype.icon;
                const numberDescKey = etype.type === "medical" ? "emergency.number108Desc" : "emergency.number112Desc";
                const descKey = etype.type === "medical" ? "emergency.call108" : "emergency.call112";
                return (
                  <button
                    key={etype.type}
                    onClick={() => handleEmergencyTypeSelect(etype)}
                    className={`w-full flex items-center gap-3 text-white rounded-lg p-3 min-h-[56px] shadow-md transition-colors duration-150 ${etype.color}`}
                  >
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                      <Icon size={22} />
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <p className="text-base font-bold">{t(`emergency.${etype.type}`)}</p>
                      <p className="text-xs text-white/80">{t(numberDescKey)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xl font-extrabold">{etype.number}</p>
                      <p className="text-xs text-white/90 font-medium">{t(descKey)}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {callInitiated && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center animate-fade-in">
                <Phone size={20} className="text-emerald-600 mx-auto mb-2 animate-pulse" />
                <p className="text-sm font-bold text-emerald-800">{t("emergency.callInitiatedTo")} {selectedType?.number}</p>
                <button
                  onClick={() => setStep(2)}
                  className="mt-3 text-sm font-semibold text-red-600 hover:text-red-700"
                >
                  {t("emergency.continueNext")}
                </button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-in">
            <h2 className="text-lg font-bold text-slate-900 mb-1">{t("emergency.recordEvidenceOptional")}</h2>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 mb-3">
              {videoUrl ? (
                <div>
                  <div className="flex items-center gap-2 text-emerald-600 mb-3">
                    <CheckCircle size={18} />
                    <p className="text-sm font-bold">{t("emergency.videoRecorded")}</p>
                  </div>
                  <video
                    src={videoUrl}
                    controls
                    className="w-full rounded-lg bg-black mb-3"
                    style={{ maxHeight: "140px" }}
                  />
                  <button
                    onClick={resetRecording}
                    className="flex items-center gap-1.5 text-sm text-red-600 font-semibold"
                  >
                    <RotateCcw size={14} />
                    {t("emergency.recordAgain")}
                  </button>
                </div>
              ) : recording ? (
                <div className="text-center">
                  <video
                    ref={videoPreviewRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full rounded-lg bg-black mb-3"
                    style={{ maxHeight: "140px" }}
                  />
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" />
                    <span className="text-lg font-bold text-slate-900">{timeLeft}s</span>
                  </div>
                  <button
                    onClick={stopRecording}
                    className="bg-red-600 text-white font-bold px-6 py-2.5 rounded-lg"
                  >
                    {t("emergency.stopRecording")}
                  </button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Camera size={48} className="text-slate-300 mx-auto mb-3" />
                  {videoError ? (
                    <div className="flex items-center gap-2 justify-center text-red-600 mb-3">
                      <XCircle size={16} />
                      <p className="text-sm">{t("emergency.cameraDenied")}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 mb-3">{t("emergency.tapToStartRecording")}</p>
                  )}
                  <button
                    onClick={startRecording}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-colors duration-150"
                  >
                    <Video size={18} />
                    {t("emergency.startRecording")}
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(3)}
                className="flex-1 bg-slate-200 text-slate-700 font-semibold py-4 min-h-[48px] rounded-lg transition-colors duration-150 hover:bg-slate-300"
              >
                {t("emergency.skip")}
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!videoBlob}
                className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-bold py-4 min-h-[48px] rounded-lg transition-colors duration-150"
              >
                {t("emergency.next")}
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-in">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">{t("emergency.yourPhoneNumber")}</h2>
            <p className="text-xs text-slate-500 mb-4 sm:mb-6">{t("emergency.phoneRequiredDesc")}</p>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 mb-3">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                {t("emergency.phoneLabel")}
              </label>
              <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-red-500/30 focus-within:border-red-500">
                <span className="bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-600 border-r border-slate-300">
                  +91
                </span>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setPhoneNumber(val);
                    setPhoneError("");
                  }}
                  className="flex-1 px-3 py-3 text-base outline-none"
                  placeholder={t("emergency.enterPhonePlaceholder")}
                  maxLength={10}
                />
              </div>
              {phoneError && <p className="text-xs text-red-600 mt-1.5 font-medium">{phoneError}</p>}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3">
              <div className="flex items-center gap-1.5 text-amber-800">
                <Shield size={14} />
                <p className="text-xs font-semibold">{t("emergency.phonePrivacyNote")}</p>
              </div>
            </div>

            {submitError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-medium">
                {submitError}
              </div>
            )}
            <button
              onClick={handleSubmit}
              disabled={submitting || !phoneNumber}
              className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-bold py-3 min-h-[48px] rounded-lg shadow-md transition-colors duration-150"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {t("emergency.submitting")}
                </>
              ) : (
                <>
                  {t("emergency.submitReport")}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Language button above sticky call bar (112/108/100) so both remain visible on mobile */}
      <div className="fixed right-3 sm:right-4 z-[60] bottom-[4.5rem] sm:bottom-6 pr-[env(safe-area-inset-right)]">
        <LanguageSwitcher />
      </div>
      <StickyCallBar />
    </div>
  );
};

export default ReportEmergencyPage;
